import { motion } from 'framer-motion'
import { Landmark } from 'lucide-react'
import type { SlideProps } from '@/components/SlideWizard'
import { useSlideCopy } from '@/lib/i18n'
import { resolveSite } from '@/lib/site'
import { copy } from './Slide8CreditLetter.copy'

const ease = [0.16, 1, 0.3, 1] as const

export default function Slide8CreditLetter(_props: SlideProps) {
  const c = useSlideCopy(copy, {
    skipSpellingFor: ['bankName', 'fakePhone', 'refLine', 'addressLine2', 'letterDate'],
  })
  const VERIFY_ROUTES = [c.verifyRoute1, c.verifyRoute2]
  // The Qatar face never shows a real bank brand: a neutral bank mark sits
  // beside the generic "Your Bank" / "بنكك" wordmark from the copy.
  const showBankMark = resolveSite() === 'qatar'

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 mx-auto max-w-5xl">
      <div className="grid w-full grid-cols-1 items-start gap-10 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] lg:items-center lg:gap-8">
        <motion.div
          initial={{ opacity: 0, y: 22, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.45, delay: 0.08, ease }}
          className="mx-auto w-full max-w-[30rem]"
        >
          <div className="relative h-[29rem] sm:h-[31rem]">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.12, ease }}
              className="absolute inset-x-0 bottom-0 z-10 h-[11.5rem] rounded-[1.2rem] bg-[#f4efe6] shadow-[0_18px_50px_-42px_rgba(15,23,42,0.35)]"
            />

            <motion.div
              initial={{ rotateX: 0 }}
              animate={{ rotateX: -168 }}
              transition={{ duration: 1.35, delay: 0.4, ease }}
              style={{ transformPerspective: 1000 }}
              className="absolute inset-x-4 bottom-[5.72rem] z-[15] h-[5.65rem] origin-top bg-[#fbf7ef] shadow-[0_10px_24px_-24px_rgba(15,23,42,0.18)] [clip-path:polygon(0_0,100%_0,50%_84%)]"
            />

            <motion.div
              initial={{ opacity: 0, y: 150 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, delay: 1.2, ease }}
              className="absolute inset-x-0 top-0 z-20"
            >
              <div className="relative mx-auto rounded-[0.35rem] border border-slate-300 bg-[#fffdf8] px-7 py-6 shadow-[0_24px_80px_-42px_rgba(15,23,42,0.28)] sm:px-9 sm:py-7">
                <div className="absolute inset-x-0 top-0 h-16 rounded-t-[0.35rem] bg-slate-50" />
                <div className="absolute inset-x-0 bottom-0 h-4 bg-[linear-gradient(to_top,rgba(15,23,42,0.12),rgba(15,23,42,0))]" />
                <div className="relative">
                  <div className="flex items-center justify-between border-b border-slate-200/90 pb-4">
                    <div className="flex items-center gap-3">
                      {showBankMark && (
                        <span
                          aria-hidden="true"
                          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-slate-800 text-white"
                        >
                          <Landmark className="h-4 w-4" strokeWidth={2} />
                        </span>
                      )}
                      <p className="text-[15px] font-semibold tracking-tight text-slate-800">
                        {c.bankName}
                      </p>
                      <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400">
                        {c.bankSubtitle}
                      </p>
                    </div>
                    <div className="text-end text-[10px] leading-relaxed text-slate-400">
                      <p>{c.addressLine1}</p>
                      <p>{c.addressLine2}</p>
                    </div>
                  </div>

                  <div className="mt-5 flex items-start justify-between text-[11px] leading-relaxed text-slate-400">
                    <div>
                      <p className="font-medium text-slate-500">{c.letterDate}</p>
                      <p>{c.refLine}</p>
                    </div>
                    <div className="text-end">
                      <p>{c.privateLine1}</p>
                      <p>{c.privateLine2}</p>
                    </div>
                  </div>

                  <div className="mt-6 space-y-3 text-[13.5px] leading-relaxed text-slate-600 sm:text-sm">
                    <p>{c.salutation}</p>
                    <p>{c.bodyOpening}</p>
                    <p>
                      {c.bodyBefore}
                      <span className="font-semibold text-slate-800">
                        {c.fakePhone}
                      </span>
                      {c.bodyAfter}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.18, ease }}
              className="absolute inset-x-0 bottom-0 z-40 h-[8.9rem] overflow-hidden rounded-b-[1.2rem] bg-[#fbf7ef]"
            >
              <div className="absolute inset-x-0 bottom-0 h-full bg-[#f9f4eb]" />
              <div className="absolute inset-x-0 bottom-0 h-full [clip-path:polygon(0_100%,0_0,50%_58%,100%_0,100%_100%)] bg-[#f5efe5]" />
            </motion.div>
          </div>
        </motion.div>

        <div className="mx-auto w-full max-w-xl lg:max-w-none lg:border-s lg:border-slate-200/80 lg:ps-10">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 1.95, ease }}
            className="pb-6"
          >
            <div className="flex items-center gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-rose-50 text-rose-500">
                <svg
                  className="h-3 w-3"
                  viewBox="0 0 12 12"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                >
                  <path d="M3 9L9 3M3 3l6 6" />
                </svg>
              </span>
              <p className="text-xl sm:text-[1.4rem] font-semibold text-slate-800">
                {c.whatTheyWantLabel}
              </p>
            </div>
            <p className="mt-2 ps-9 max-w-md text-base leading-relaxed text-slate-500 sm:text-lg">
              {c.whatTheyWant}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 2.18, ease }}
            className="pt-6"
          >
            <div className="flex items-center gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                <svg
                  className="h-3 w-3"
                  viewBox="0 0 12 12"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                >
                  <path d="M2.5 6.5L5 9l4.5-6" />
                </svg>
              </span>
              <p className="text-xl sm:text-[1.4rem] font-semibold text-slate-800">
                {c.saferMoveLabel}
              </p>
            </div>
            <p className="mt-2 ps-9 text-base leading-relaxed text-slate-500 sm:text-lg">
              {c.saferMove}
            </p>
            <div className="mt-4 space-y-3 ps-9">
              {VERIFY_ROUTES.map((route, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: 8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.42, delay: 2.34 + i * 0.12, ease }}
                  className="flex items-start gap-2.5"
                >
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center text-emerald-600">
                    <svg
                      className="h-3.5 w-3.5"
                      viewBox="0 0 12 12"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.7"
                    >
                      <path d="M2.5 6.5L5 9l4.5-6" />
                    </svg>
                  </span>
                  <p className="text-base leading-relaxed text-slate-500 sm:text-lg">
                    {route}
                  </p>
                </motion.div>
              ))}
            </div>
            <motion.p
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.42, delay: 2.78, ease }}
              className="mt-5 ps-9 text-base leading-relaxed text-slate-400 sm:text-lg"
            >
              {c.warning}
            </motion.p>
          </motion.div>
        </div>
      </div>
    </div>
  )
}
